import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Users, Mail, DollarSign, Package, Bot, Settings, 
  ArrowRight, Command, LayoutDashboard, Headphones, Plus
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const CommandBar = ({ isOpen, onClose, leads = [], isDark }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Navigation commands
  const navigationCommands = [
    { id: 'overview', label: 'Go to Overview', icon: LayoutDashboard, action: () => navigate('/dealer'), category: 'Navigate' },
    { id: 'pipeline', label: 'Go to Pipeline', icon: Users, action: () => navigate('/dealer?tab=pipeline'), category: 'Navigate' },
    { id: 'email', label: 'Go to Email Center', icon: Mail, action: () => navigate('/dealer?tab=email'), category: 'Navigate' },
    { id: 'sales', label: 'Go to Sales', icon: DollarSign, action: () => navigate('/dealer?tab=sales'), category: 'Navigate' },
    { id: 'inventory', label: 'Go to Inventory', icon: Package, action: () => navigate('/dealer?tab=inventory'), category: 'Navigate' },
    { id: 'crm', label: 'Go to CRM Hub', icon: Headphones, action: () => navigate('/dealer?tab=crm'), category: 'Navigate' },
    { id: 'research', label: 'Go to Sales Assistant', icon: Bot, action: () => navigate('/dealer?tab=research'), category: 'Navigate' },
  ];

  // Action commands
  const actionCommands = [
    { id: 'new-email', label: 'Compose New Email', icon: Mail, action: () => navigate('/dealer?tab=email'), category: 'Actions' },
    { id: 'record-sale', label: 'Record New Sale', icon: Plus, action: () => navigate('/dealer?tab=sales'), category: 'Actions' },
  ];

  // Filter leads based on query
  const filteredLeads = query.length > 1 
    ? leads.filter(lead => 
        lead.name?.toLowerCase().includes(query.toLowerCase()) ||
        lead.email?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5).map(lead => ({
        id: `lead-${lead.id}`,
        label: lead.name || lead.email,
        sublabel: lead.email,
        icon: Users,
        action: () => {
          // Navigate to pipeline and select lead
          navigate('/dealer?tab=pipeline');
          onClose();
          // Emit custom event to open lead modal
          window.dispatchEvent(new CustomEvent('openLeadModal', { detail: { leadId: lead.id } }));
        },
        category: 'Leads'
      }))
    : [];

  // Combine all items
  const allItems = [
    ...filteredLeads,
    ...(query ? navigationCommands.filter(cmd => cmd.label.toLowerCase().includes(query.toLowerCase())) : navigationCommands),
    ...(query ? actionCommands.filter(cmd => cmd.label.toLowerCase().includes(query.toLowerCase())) : actionCommands),
  ];

  // Group by category
  const groupedItems = allItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const flatItems = Object.values(groupedItems).flat();

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[selectedIndex]) {
        flatItems[selectedIndex].action();
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [flatItems, selectedIndex, onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`max-w-xl p-0 overflow-hidden border-0 shadow-2xl ${isDark ? 'bg-slate-900' : 'bg-white'}`}
        data-testid="command-bar"
      >
        <VisuallyHidden.Root>
          <DialogTitle>Command Bar - Search leads, navigate, or take action</DialogTitle>
        </VisuallyHidden.Root>
        {/* Search Input */}
        <div className={`flex items-center gap-3 px-4 py-3 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
          <Search size={20} className={isDark ? 'text-slate-400' : 'text-slate-400'} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search leads, navigate, or take action..."
            className={`flex-1 text-sm bg-transparent border-0 outline-none placeholder:text-slate-400 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
            data-testid="command-search-input"
          />
          <kbd className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
            isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
          }`}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto py-2">
          {Object.keys(groupedItems).length === 0 ? (
            <div className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              <p className="text-sm">No results found</p>
            </div>
          ) : (
            Object.entries(groupedItems).map(([category, items]) => (
              <div key={category} className="mb-2">
                <p className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider ${
                  isDark ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  {category}
                </p>
                {items.map((item, idx) => {
                  const globalIndex = flatItems.findIndex(i => i.id === item.id);
                  const isSelected = globalIndex === selectedIndex;
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.id}
                      onClick={() => { item.action(); onClose(); }}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected 
                          ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900'
                          : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`command-item-${item.id}`}
                    >
                      <Icon size={18} className={isSelected ? '' : isDark ? 'text-slate-500' : 'text-slate-400'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.label}</p>
                        {item.sublabel && (
                          <p className={`text-xs truncate ${isSelected ? 'opacity-70' : isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {item.sublabel}
                          </p>
                        )}
                      </div>
                      <ArrowRight size={16} className={`shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-4 py-2 border-t text-xs ${
          isDark ? 'border-slate-800 text-slate-500' : 'border-slate-200 text-slate-400'
        }`}>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>↑</kbd>
              <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>↵</kbd>
              to select
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommandBar;
